import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO,
  STATUS_ALLOWED_FOR_VALIDATION_ACTION,
} from '@app/features/contracts/validation/validation-request-service';
import { ValidationRequestType } from '@app/features/contracts/validation/validation-request.model';
import { UserRole } from '@app/features/users/user.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { ValidationCircuits } from '@app/features/contracts/validation/validation-circuit';
import { ValidationSubjectItem } from '@app/features/contracts/validation/validation-subject.model';
import { RxjsDirective } from '@app/shared/rxjs.directive';

@Component({
  selector: 'app-button-accept-validation-request',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './button-accept-validation-request.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./button-accept-validation-request.scss'],
})
export class ButtonAcceptValidationRequest extends RxjsDirective {
  /** Le sujet validé : un contrat d'embauche, ou un avenant. */
  contract = input.required<ValidationSubjectItem>();
  private readonly validationCircuits = inject(ValidationCircuits);
  protected circuit = () => this.validationCircuits.of(this.contract());
  validationType = input.required<ValidationRequestType>();
  validationAccepted = output<void>();

  authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  rolesAllowedToByPassRequestedTo: UserRole[] = [];

  constructor() {
    super();
    effect(() => {
      this.rolesAllowedToByPassRequestedTo =
        ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO[this.validationType()] ?? [];
    });
  }
  get canAcceptValidation(): boolean {
    const validationRequests = this.contract().validationRequests ?? [];
    const allowedStatuses = STATUS_ALLOWED_FOR_VALIDATION_ACTION[this.validationType()] ?? [];
    const pendingValidationRequests = validationRequests.filter(
      (req) => req.type === this.validationType() && req.status === 'PENDING',
    );
    const hasPendingValidationRequest = pendingValidationRequests.length > 0;
    const isRequestedToCurrentUser = pendingValidationRequests.some(
      (req) => req.requestedTo === this.authenticatedUserService.userId,
    );
    const isBypassRole = this.rolesAllowedToByPassRequestedTo.some((role) =>
      this.authenticatedUserService.realmRoles.includes(role),
    );

    return (
      allowedStatuses.includes(this.contract().status) &&
      hasPendingValidationRequest &&
      (isRequestedToCurrentUser || isBypassRole)
    );
  }

  acceptValidation() {
    this.register(
      this.circuit()
        .accept(this.contract().id)
        .subscribe(() => this.validationAccepted.emit()),
    );
  }
}
